import { useEffect, useState } from "react";
import FieldLoader from "../../../../components/UI/FieldLoader";
import PERMISSIONS from "../../../../utils/AclPermissions";
import Buttons from "../../../../components/UI/Buttons";
import { isSet } from "../../../../utils/commonUtils";
import FormInputs from "../../../../components/UI/FormInputs";
import EmrVisitsService from "../../../../services/EmrVisitsService";
import { createPortal } from "react-dom";
import Alert from "../../../../components/UI/ConfirmBox/Alert";
import FileManagerService from "../../../../services/FileManagerService";
import EmrStatusBar from "../ComplaintPage/EmrStatusBar";
import SendMail from "./SendMail";
import { Modal } from "react-bootstrap";

const ChartNote = (props) => {
  const [isLoading, setLoading] = useState();
  const [notes, setNotes] = useState("");
  const { visit_id, employeeId, selectedSubTab } = props;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sendMailModel, setSendMailModel] = useState(false);
  const [sendMailOptions, setSendMailOptions] = useState({
    includePmh: false,
    includeDemographics: false,
  });
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  // Multiselect options for print dialog
  const [printOptions, setPrintOptions] = useState({
    includePmh: false,
    includeDemographics: false,
  });
  const [orderCounts, setOrderCounts] = useState({
      open_orders: 0,
      completed_orders: 0,
      ready_for_review: 0,
    });
  useEffect(() => {
    if (visit_id && selectedSubTab) {
      fetchNotes();
    }
  }, [visit_id, selectedSubTab]);
  const fetchNotes = async () => {
    setLoading(true);
    const response = await EmrVisitsService.getChartNotes(
      selectedSubTab,
      visit_id
    );
    const { data } = response;
    setLoading(false);
    setNotes(data?.note);
  };
  const handleUpdate = async (isClear = false, paramsNote = notes) => {
    const payload = {
      note: isClear ? "" : paramsNote,
      employee_id: employeeId,
    };
    setLoading(true);
    await EmrVisitsService.saveChartNotes(selectedSubTab, visit_id, payload);
    setLoading(false);
    if (isClear) {
      setShowDeleteModal(false);
    }
    fetchNotes();
  };
  const handleNotesChange = (value) => {
    setNotes(value);
  };
  const openSendMail = () => {
    setSendMailModel(true);
  };
  const sendEmail = async (email, options = {}) => {
    const {
      includePmh = sendMailOptions.includePmh,
      includeDemographics = sendMailOptions.includeDemographics,
    } = options;
    try {
      setSendMailOptions({ includePmh, includeDemographics });
      props.Loader(true);
      const response = await EmrVisitsService.sendChartNotesEmail(
        visit_id,
        email,
        includePmh,
        includeDemographics
      );
      if (response) {
        props.Toast.success("Email sent successfully");
      } else {
        props.Toast.error("Something went wrong");
      }
      props.Loader(false);
      setSendMailModel(false);
    } catch (error) {
      props.Loader(false);
      console.error("Error sending email:", error);
    }
  };

  const handlePrintChartNotesClick = () => {
    setShowPrintOptions(true);
  };

  const printPatientDemography = async () => {
    try {
      if (props.Loader) props.Loader(true);
      const response = await EmrVisitsService.printPatientDemography(visit_id);
      const base64Data =
        typeof response === "string" ? response : response?.data || response;
      if (base64Data && typeof base64Data === "string" && base64Data.length > 0) {
        const pdfData = {
          file_content: base64Data,
          original_name: "PatientDemographics",
          mime_type: "application/pdf",
        };
        FileManagerService.printPdf(pdfData);
      } else if (props.Toast) {
        props.Toast.error("Failed to generate Patient Demographics PDF");
      }
    } catch (error) {
      console.error("Error downloading Patient Demographics PDF:", error);
      if (props.Toast) {
        props.Toast.error("Error downloading Patient Demographics PDF");
      }
    } finally {
      if (props.Loader) props.Loader(false);
    }
  };

  const handleConfirmPrintOptions = async () => {
    const { includePmh, includeDemographics } = printOptions;
    try {
      if (props.Loader) props.Loader(true);
      setShowPrintOptions(false);

      // Print preview flows (not download)
      // Only PMH -> print chart note with PMH
      if (includePmh && !includeDemographics) {
        await doPrintChartNotes(true);
      }
      // Only Demographics -> print combined PDF with chart note without PMH
      else if (!includePmh && includeDemographics) {
        await doPrintCombinedChartNote(false);
      }
      // Both -> print single combined PDF (demography first, then chart note with PMH)
      else if (includePmh && includeDemographics) {
        await doPrintCombinedChartNote(true);
      }
      // None -> print chart note without PMH
      else {
        await doPrintChartNotes(false);
      }
    } catch (error) {
      if (props.Toast) props.Toast.error("Failed to generate print preview.");
      console.error("Confirm print options error:", error);
    } finally {
      if (props.Loader) props.Loader(false);
    }
  };

  const downloadPatientDemography = async (keepLoaderOn = false) => {
    try {
      if (props.Loader) props.Loader(true);
      const response = await EmrVisitsService.printPatientDemography(visit_id);
      const base64Data =
        typeof response === "string" ? response : response?.data || response;
      if (base64Data && typeof base64Data === "string" && base64Data.length > 0) {
        const pdfData = {
          file_content: base64Data,
          original_name: "PatientDemographics",
          mime_type: "application/pdf",
        };
        FileManagerService.downloadPdf(pdfData);
      } else if (props.Toast) {
        props.Toast.error("Failed to generate Patient Demographics PDF");
      }
    } catch (error) {
      console.error("Error downloading Patient Demographics PDF:", error);
      if (props.Toast) {
        props.Toast.error("Error downloading Patient Demographics PDF");
      }
    } finally {
      if (!keepLoaderOn && props.Loader) props.Loader(false);
    }
  };
  const doPrintChartNotes = async (includePmh) => {
    props.Loader(true);
    try {
      const response = await EmrVisitsService.getChartNotes(
        "PRINT_CHART",
        visit_id,
        includePmh
      );
      const pdfData = {
        file_content: response.data,
        original_name: "ChartNotes",
        mime_type: "application/pdf",
      };
      FileManagerService.printPdf(pdfData);
    } finally {
      props.Loader(false);
    }
  };
  const doDownloadChartNotes = async (includePmh, keepLoaderOn = false) => {
    props.Loader(true);
    try {
      const response = await EmrVisitsService.getChartNotes(
        "PRINT_CHART",
        visit_id,
        includePmh
      );
      const pdfData = {
        file_content: response.data,
        original_name: "ChartNotes",
        mime_type: "application/pdf",
      };
      FileManagerService.downloadPdf(pdfData);
    } finally {
      if (!keepLoaderOn) props.Loader(false);
    }
  };

  const doDownloadCombinedChartNote = async (includePmh) => {
    props.Loader(true);
    try {
      const response = await EmrVisitsService.getCombinedChartNotePdf(
        visit_id,
        includePmh
      );
      const base64Data =
        typeof response === "string" ? response : response?.data ?? response;
      if (base64Data && typeof base64Data === "string" && base64Data.length > 0) {
        const pdfData = {
          file_content: base64Data,
          original_name: "ChartNotesWithDemographics",
          mime_type: "application/pdf",
        };
        FileManagerService.downloadPdf(pdfData);
      } else if (props.Toast) {
        props.Toast.error("Failed to generate combined PDF");
      }
    } catch (error) {
      if (props.Toast) props.Toast.error("Failed to generate combined PDF");
      console.error("Combined chart note download error:", error);
    } finally {
      props.Loader(false);
    }
  };

  const doPrintCombinedChartNote = async (includePmh) => {
    props.Loader(true);
    try {
      const response = await EmrVisitsService.getCombinedChartNotePdf(
        visit_id,
        includePmh
      );
      const base64Data =
        typeof response === "string" ? response : response?.data ?? response;
      if (base64Data && typeof base64Data === "string" && base64Data.length > 0) {
        const pdfData = {
          file_content: base64Data,
          original_name: "ChartNotesWithDemographics",
          mime_type: "application/pdf",
        };
        FileManagerService.printPdf(pdfData);
      } else if (props.Toast) {
        props.Toast.error("Failed to generate combined PDF");
      }
    } catch (error) {
      if (props.Toast) props.Toast.error("Failed to generate combined PDF");
      console.error("Combined chart note print error:", error);
    } finally {
      props.Loader(false);
    }
  };
  return (
    <>
      {showDeleteModal &&
        createPortal(
          <Alert
            onDelete={() => handleUpdate(true, "")}
            onClose={() => setShowDeleteModal(false)}
            content="Do you really want to Clear this record?"
          />,
          document.body
        )}
        {sendMailModel &&
          createPortal(
            <SendMail
              isopen={sendMailModel}
              onClose={() => setSendMailModel(false)}
              onSendEmail={sendEmail}
              initialIncludePmh={sendMailOptions.includePmh}
              initialIncludeDemographics={sendMailOptions.includeDemographics}
            />,
            document.body
          )
        }

        {showPrintOptions && (
          <Modal
            show={showPrintOptions}
            onHide={() => setShowPrintOptions(false)}
            dialogClassName="hResponsive"
            centered
            keyboard={false}
            backdrop="static"
          >
            <div className="sh_modalBox">
              <Modal.Header closeButton>
                <Modal.Title>
                  <h6 className="text-lg mb-0 font-weight-bold">Print Chart Notes</h6>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="mb-2" style={{ fontSize: "15px", color: "#4b5563" }}>
                  Choose what to include in this print.
                </div>
                <div className="form-group">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="printOptionPmh"
                      checked={printOptions.includePmh}
                      onChange={(e) =>
                        setPrintOptions((prev) => ({
                          ...prev,
                          includePmh: e.target.checked,
                        }))
                      }
                    />
                    <label className="form-check-label" htmlFor="printOptionPmh">
                      Include PMH
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="printOptionDemography"
                      checked={printOptions.includeDemographics}
                      onChange={(e) =>
                        setPrintOptions((prev) => ({
                          ...prev,
                          includeDemographics: e.target.checked,
                        }))
                      }
                    />
                    <label className="form-check-label" htmlFor="printOptionDemography">
                      Include Patient Demographics
                    </label>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer className="d-flex justify-content-center">
                <button
                  type="button"
                  className="btn btn-secondary sh_btnsecondary mr-2"
                  onClick={() => setShowPrintOptions(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary sh_btnprimary mr-2"
                  onClick={handleConfirmPrintOptions}
                >
                  Continue
                </button>
              </Modal.Footer>
            </div>
          </Modal>
        )}

         <div className="sh_cardHeader mb-3 rounded-2" style={{ background: "linear-gradient(90deg, #FFF7DC 0%, #F7E9E5 100%)",}}>
            <div className="d-flex justify-content-center">
              <div className="form-group d-flex mb-0 justify-content-between align-items-center">
               <EmrStatusBar visitDetails={props.visitDetails} />
              </div>
            </div>
          </div>
      <div className="sh_cardBody py-0" >
          <div className="sh_cardHeader px-0">
          <div className="row mb-2">
            <div className="col-md-4 col-6">
              <div className="d-flex align-items-center justify-content-between sh_statusIndicatorbox blue-orichid px-4 py-3 rounded-4 shadow-sm">
                {/* Left Section */}
                <div className="d-flex align-items-center gap-3">
                  <span
                    className="sh_statusIndicator bg-white "
                    style={{ width: "56px", height: "56px" }}
                  >
                    {/* Your SVG icon */}
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M7 12H10"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M7 16H8"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.9749 14.3V12.6C18.9749 11.9925 18.4824 11.5 17.8749 11.5H15.1249C14.5174 11.5 14.0249 11.9925 14.0249 12.6V14.3"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <rect
                        x="11"
                        y="14.3"
                        width="11"
                        height="7.7"
                        rx="1.98"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M15.25 18H17.75"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M16.5 19.25V16.75"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 21H5.5C4.11929 21 3 19.8807 3 18.5V8.37167C3.00001 7.57602 3.31608 6.81296 3.8787 6.25035L6.25038 3.87868C6.81299 3.31607 7.57605 3 8.3717 3H15.5C16.8807 3 18 4.11929 18 5.5V8"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3.02344 8.00006H6.49985C7.32827 8.00006 7.99985 7.32849 7.99985 6.50006V3.02368"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
                 {/* center section */}
                  <div>
                    <p className="mb-1 fw-medium text-dark">Open Orders</p>
                    <h3 className="fw-bold mb-0">{orderCounts.open_orders}</h3>
                  </div>

                {/* Right Section */}
               <div className="text-dark">
                <p className="mb-1 small order-row">
                  <span className="order-label">Labs</span>
                  <strong className="order-value">320</strong>
                </p>
                <p className="mb-1 small order-row">
                  <span className="order-label">Xrays</span>
                  <strong className="order-value">80</strong>
                </p>
                <p className="mb-0 small order-row">
                  <span className="order-label">Others</span>
                  <strong className="order-value">50</strong>
                </p>
              </div>
              </div>
            </div>

            <div className="col-md-4 col-6">
              <div className="d-flex align-items-center justify-content-between sh_statusIndicatorbox pale-purple px-4 py-3 rounded-4 shadow-sm">
                {/* Left Section */}
                <div className="d-flex align-items-center gap-3">
                  <span
                    className="sh_statusIndicator bg-white"
                    style={{ width: "56px", height: "56px" }}
                  >
                    {/* SVG Icon */}
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M13.0052 16.4974C13.0537 17.4984 13.4528 18.4505 14.1327 19.1869C14.6757 19.8176 15.3236 20.3497 16.0479 20.7598C16.6786 21.1116 17.4515 21.0889 18.0604 20.7005C18.7169 20.2809 19.3071 19.7657 19.8117 19.1721C20.5041 18.4432 20.9264 17.4994 21.0086 16.4974C21.0134 15.9013 20.7905 15.3258 20.3854 14.8884C19.9961 14.4643 19.4483 14.2208 18.8726 14.2161C18.1498 14.1656 17.4513 14.4879 17.0207 15.0706L17.007 15.0907L16.9931 15.0706C16.6013 14.5193 15.9609 14.1989 15.2847 14.2161C14.6768 14.2058 14.0907 14.4429 13.6609 14.873C13.2311 15.3031 12.9944 15.8894 13.0052 16.4974Z"
                        stroke="#323232"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M13.0059 10.0044H17.0075"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M7.00293 9.7302L7.92231 10.5585L9.77308 8.89185"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M10.0044 21.009H8.00355C5.24097 21.009 3.00146 18.7695 3.00146 16.0069V8.00355C3.00146 5.24097 5.24097 3.00146 8.00355 3.00146H16.0069C18.7695 3.00146 21.009 5.24097 21.009 8.00355V11.0048"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M7.00293 14.7324L7.92231 15.5607L9.77308 13.894"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  </div>
                    {/* center section */}
                  <div>
                    <p className="mb-1 fw-medium text-dark">Completed Orders</p>
                    <h3 className="fw-bold mb-0">
                      {orderCounts.completed_orders}
                    </h3>
                  </div>

                {/* Right Section */}
                <div className="text-dark">
                  <p className="mb-1 small order-row">
                    <span className="order-label">Labs</span>
                    <strong className="order-value">220</strong>
                  </p>
                  <p className="mb-1 small order-row">
                    <span className="order-label">Xrays</span>
                    <strong className="order-value">40</strong>
                  </p>
                  <p className="mb-0 small order-row">
                    <span className="order-label">Others</span>
                    <strong className="order-value">30</strong>
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-4 col-6">
              <div className="d-flex align-items-center justify-content-between sh_statusIndicatorbox linear_gradient px-4 py-3 rounded-4 shadow-sm">
                {/* Left Section */}
                <div className="d-flex align-items-center gap-3">
                  <span
                    className="sh_statusIndicator bg-white"
                    style={{ width: "56px", height: "56px" }}
                  >
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 18H14"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M16.979 3H7.021C6.469 3 6.021 3.448 6.021 4V6.378C6.021 6.799 6.154 7.21 6.401 7.551L9.621 12L6.401 16.449C6.154 16.79 6.021 17.201 6.021 17.622V20C6.021 20.552 6.469 21 7.021 21H16.978C17.53 21 17.978 20.552 17.978 20V17.658C17.978 17.214 17.83 16.783 17.558 16.432L14.121 12L17.559 7.567C17.831 7.216 17.979 6.785 17.979 6.341V4C17.979 3.448 17.531 3 16.979 3Z"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
                {/* center section */}
                  <div>
                    <p className="mb-1 fw-medium text-dark">Ready for Review</p>
                    <h3 className="fw-bold mb-0">
                      {orderCounts.ready_for_review}
                    </h3>
                  </div>

                {/* Right Section */}
                <div className="text-dark">
                    <p className="mb-1 small order-row">
                      <span className="order-label">Labs</span>
                      <strong className="order-value">150</strong>
                    </p>
                    <p className="mb-1 small order-row">
                      <span className="order-label">Xrays</span>
                      <strong className="order-value">40</strong>
                    </p>
                    <p className="mb-0 small order-row">
                      <span className="order-label">Others</span>
                      <strong className="order-value">20</strong>
                    </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="sh_emrtextareabox p-1 position-relative" >
          <FieldLoader isLoading={isLoading} />
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="text-sm font-weight-bold  mb-0">Overall Notes</h5>
            <div className="d-flex" style={{ gap: "5px" }}>
              {/* <Buttons
                className="btn mb-1 btn-primary"
                clickHandler={() => handleUpdate(false, notes)}
                label={"Update"}
              /> */}
              {/* <Buttons
                acl={
                  isSet(notes, false)
                    ? PERMISSIONS.ANY_PERMISSION
                    : PERMISSIONS.NO_PERMISSION
                }
                className="btn mb-1 btn-secondary"
                clickHandler={() => setShowDeleteModal(true)}
                label={"Clear All"}
              /> */}
            </div>
          </div>
          {/* <FormInputs
            type="Editor"
            value={notes}
            formats={{
              formats: ["background"],
            }}
            modules={{
              toolbar: [
              ],
            }}
            changeHandler={handleNotesChange}
            height={"680px"}
            readOnly={true}
          /> */}
          <div className="d-flex align-items-center justify-content-end mb-2 gap-1">
            <Buttons
              type="button"
              className="btn btn-primary sh_btnprimary"
              acl={PERMISSIONS.VISITS.SEND_EMAIL}
              accessType="Hide"
              clickHandler={openSendMail}
            >
              Send Email
            </Buttons>
            <div className="dropdown">
              <Buttons
                type="button"
                acl={PERMISSIONS.VISITS.PRINT_CHARTNOTE}
                accessType="Hide"
                className="btn btn-primary sh_btnprimary"
                clickHandler={handlePrintChartNotesClick}
              >
                Print Chart Notes
              </Buttons>
              <ul className="dropdown-menu dropdown-menu-end shadow">
                <li>
                  <button type="button" className="dropdown-item" onClick={() => doPrintChartNotes(true)}>
                    Chart note with PMH
                  </button>
                </li>
                <li>
                  <button type="button" className="dropdown-item" onClick={() => doPrintChartNotes(false)}>
                    Chart note without PMH
                  </button>
                </li>
              </ul>
            </div>
            <div className="dropdown">
              <ul className="dropdown-menu dropdown-menu-end shadow">
                <li>
                  <button type="button" className="dropdown-item" onClick={() => doDownloadChartNotes(true)}>
                    Chart note with PMH
                  </button>
                </li>
                <li>
                  <button type="button" className="dropdown-item" onClick={() => doDownloadChartNotes(false)}>
                    Chart note without PMH
                  </button>
                </li>
              </ul>
            </div>
          </div>
              
            <iframe
            style={{
              width: "100%",
              height: "680px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "5px",
              backgroundColor: "#fff",
            }}
            srcDoc={notes || "<p>No notes available</p>"}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          />
        </div>
      </div>
    </>
  );
};

export default ChartNote;
